import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';

const values = [
  {
    icon: 'ri-focus-3-line',
    title: '清晰定位',
    text: '专注影视内容分享与资讯更新，用结构化信息帮用户快速找到想要的影片与动态。',
  },
  {
    icon: 'ri-line-chart-line',
    title: '持续更新',
    text: '每日整理票房动态、行业消息与影片资料，让影迷第一时间掌握影坛变化。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '真实克制',
    text: '只提供资讯与资料展示，不含播放与下载，拒绝误导，尊重每一部作品。',
  },
];

const team = [
  {
    name: '陈屿',
    role: '主编 / 内容策划',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20calm%20asian%20male%20editor%20in%20a%20navy%20shirt%2C%20clean%20neutral%20studio%20background%2C%20soft%20natural%20lighting%2C%20editorial%20headshot%2C%20high%20detail&width=400&height=400&seq=team-avatar-001&orientation=squarish',
  },
  {
    name: '林晚',
    role: '影评作者',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20warm%20asian%20female%20writer%20in%20a%20cream%20knitwear%2C%20clean%20neutral%20studio%20background%2C%20soft%20natural%20lighting%2C%20editorial%20headshot%2C%20high%20detail&width=400&height=400&seq=team-avatar-002&orientation=squarish',
  },
  {
    name: '周牧',
    role: '数据分析',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20young%20asian%20male%20analyst%20with%20glasses%2C%20clean%20neutral%20studio%20background%2C%20soft%20natural%20lighting%2C%20editorial%20headshot%2C%20high%20detail&width=400&height=400&seq=team-avatar-003&orientation=squarish',
  },
  {
    name: '苏可',
    role: '社区运营',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20friendly%20asian%20female%20community%20manager%2C%20clean%20neutral%20studio%20background%2C%20soft%20natural%20lighting%2C%20editorial%20headshot%2C%20high%20detail&width=400&height=400&seq=team-avatar-004&orientation=squarish',
  },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 草民电影',
    description:
      '了解草民电影的网站定位、发展目标与团队。我们专注影视内容分享与资讯更新，为影迷提供最新电影动态与精选影片推荐。',
  });

  return (
    <div className="w-full">
      <section className="w-full bg-primary-800 px-4 py-12 md:px-6 md:py-16">
        <div className="[&_a]:text-background-300 [&_span]:text-background-300">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        </div>
        <h1 className="mt-5 font-heading text-3xl font-bold text-background-50 md:text-4xl">
          关于草民电影
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
          一个为影视爱好者打造的资讯与观影参考平台，专注内容分享与资讯更新。
        </p>
      </section>

      <section className="w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们是谁</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              草民电影由一群热爱电影的普通观众发起。我们不是影评权威，只是一群愿意花时间把片子看仔细、把资料整理清楚的人。
              每天，我们都会筛选当天的电影新闻，补充影片的导演、主演、评分与剧情简介，让想了解一部电影的人，能用最短的时间得到最需要的信息。
            </p>
            <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
              我们相信，好电影值得被认真对待，而了解一部好电影，应该是一件简单的事。
            </p>

            <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们的目标</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              让每一位来到这里的影迷，都能快速找到感兴趣的电影与资讯，并在这个基础上，形成属于自己的观影清单。
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {values.map((value) => (
              <div
                key={value.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-700">
                  <i className={value.icon} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{value.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{value.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-12 md:px-6 md:py-16">
        <h2 className="mb-8 text-center font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          团队介绍
        </h2>
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {team.map((member) => (
            <div key={member.name} className="flex flex-col items-center text-center">
              <div className="h-24 w-24 overflow-hidden rounded-full bg-background-200">
                <img
                  src={member.avatar}
                  alt={`${member.name} 头像`}
                  title={`${member.name} ${member.role}`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{member.name}</h3>
              <p className="mt-1 text-xs text-foreground-500">{member.role}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-6 md:py-16">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">版权声明</h2>
          <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
            本站所有影片名称、海报与资讯内容均为演示性质的虚构示例，仅用于展示页面结构与信息组织方式。
            本站不提供任何影片的在线播放、下载或资源分发服务。如涉及真实作品信息，请以官方发布为准。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              联系我们
            </Link>
            <Link
              to="/news"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览资讯
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}